import behaviourCsvUploadConstants from "@/constants/behaviourCsvUploadConstants.js"

/**
 * @param {string} uploadKind
 * @returns {typeof behaviourCsvUploadConstants.UPLOAD_DEFINITIONS[keyof typeof behaviourCsvUploadConstants.UPLOAD_DEFINITIONS] | null}
 */
export function getBehaviourCsvUploadDefinition(uploadKind) {
  return behaviourCsvUploadConstants.UPLOAD_DEFINITIONS?.[uploadKind] || null
}

/**
 * @param {string} uploadKind
 * @returns {boolean}
 */
export function isValidBehaviourCsvUploadKind(uploadKind) {
  return behaviourCsvUploadConstants.UPLOAD_KIND_VALUES.includes(uploadKind)
}

/**
 * @param {string} uploadKind
 * @param {Record<string, string>} terminology
 * @param {(key: string, values?: Record<string, string>) => string} t
 * @returns {string}
 */
export function resolveBehaviourCsvUploadModalTitle(
  uploadKind,
  terminology,
  t
) {
  const def = getBehaviourCsvUploadDefinition(uploadKind)
  if (!def?.titleI18nKey) return ""
  return t(def.titleI18nKey, terminology || {})
}

/**
 * @param {string} uploadKind
 * @param {Record<string, string>} terminology
 * @param {(key: string, values?: Record<string, string>) => string} t
 * @returns {string}
 */
export function resolveBehaviourCsvUploadFileLabel(uploadKind, terminology, t) {
  const def = getBehaviourCsvUploadDefinition(uploadKind)
  if (!def?.fileLabelI18nKey) return ""
  return t(def.fileLabelI18nKey, terminology || {})
}

/**
 * @param {File | null | undefined} file
 * @returns {FormData}
 */
export function buildBehaviourCsvUploadFormData(file) {
  const formData = new FormData()
  if (file) {
    formData.append("file", file)
  }
  return formData
}

/**
 * @typedef {Object} BehaviourCsvUploadRowResult
 * @property {number} row
 * @property {boolean} success
 * @property {string} [error]
 */

/** API status when some rows imported and some failed (HTTP 200). */
export const BEHAVIOUR_CSV_UPLOAD_STATUS_PARTIAL_SUCCESS = "partial_success"

/**
 * @typedef {Object} BehaviourCsvUploadResult
 * @property {number} totalRows
 * @property {number} inserted
 * @property {number} failed
 * @property {BehaviourCsvUploadRowResult[]} rowResults
 * @property {string} status
 * @property {string} [title]
 * @property {string} [summary]
 * @property {string} [message]
 */

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {Record<string, unknown>}
 */
function normalizeBehaviourCsvUploadPayload(body) {
  if (!body || typeof body !== "object") {
    return {}
  }

  const nested = body?.data
  const hasImportFields = (payload) =>
    payload &&
    typeof payload === "object" &&
    (Array.isArray(payload?.row_results) ||
      Array.isArray(payload?.import_errors) ||
      Number.isFinite(Number(payload?.total_rows)))

  if (hasImportFields(nested)) {
    return nested
  }

  if (hasImportFields(body)) {
    return body
  }

  return nested && typeof nested === "object" ? nested : body
}

/**
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {BehaviourCsvUploadRowResult[]}
 */
function parseBehaviourCsvUploadRowResults(data) {
  const rowResults = Array.isArray(data?.row_results) ? data.row_results : []
  if (rowResults.length) {
    return rowResults.map((item) => {
      const error = String(item?.error ?? "").trim()
      return {
        row: Number(item?.row) || 0,
        success: item?.success === true || (item?.success !== false && !error),
        error
      }
    })
  }

  const importErrors = Array.isArray(data?.import_errors)
    ? data.import_errors
    : []

  return importErrors.map((item) => ({
    row: Number(item?.row) || 0,
    success: false,
    error: String(item?.error ?? "").trim()
  }))
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {boolean}
 */
export function isBehaviourCsvUploadImportResponse(body) {
  const data = normalizeBehaviourCsvUploadPayload(body)
  if (!data || typeof data !== "object") {
    return false
  }

  return (
    Array.isArray(data?.row_results) ||
    Array.isArray(data?.import_errors) ||
    Number.isFinite(Number(data?.total_rows))
  )
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {BehaviourCsvUploadResult}
 */
export function parseBehaviourCsvUploadResponse(body) {
  const data = normalizeBehaviourCsvUploadPayload(body)

  return {
    totalRows: Number(data?.total_rows) || 0,
    inserted: Number(data?.inserted) || 0,
    failed: Number(data?.failed) || 0,
    rowResults: parseBehaviourCsvUploadRowResults(data),
    status: String(body?.status ?? data?.status ?? ""),
    title: String(data?.title ?? body?.title ?? "").trim(),
    summary: String(data?.summary ?? body?.summary ?? "").trim(),
    message: String(body?.message ?? data?.message ?? "").trim()
  }
}

/**
 * @param {unknown} err
 * @returns {string}
 */
export function parseBehaviourCsvUploadApiError(err) {
  const data = err?.response?.data
  if (isBehaviourCsvUploadImportResponse(data)) {
    const parsed = parseBehaviourCsvUploadResponse(data)
    return parsed.summary || parsed.message || ""
  }
  return String(data?.error ?? data?.message ?? err?.message ?? "").trim() || ""
}

/**
 * @param {BehaviourCsvUploadRowResult[] | null | undefined} rowResults
 * @returns {string[]}
 */
export function getBehaviourCsvUploadRowMessages(rowResults) {
  return (rowResults || [])
    .map((row) => {
      const rowNumber = Number(row?.row)
      const error = String(row?.error ?? "").trim()
      const isFailed = row?.success === false || Boolean(error)

      if (!isFailed) {
        return ""
      }

      const message = error || "Unknown error"
      return Number.isFinite(rowNumber) && rowNumber > 0
        ? `Row ${rowNumber}: ${message}`
        : message
    })
    .filter(Boolean)
}

/**
 * @param {BehaviourCsvUploadRowResult[] | null | undefined} rowResults
 * @returns {string[]}
 */
export function getBehaviourCsvUploadFailedRowMessages(rowResults) {
  return getBehaviourCsvUploadRowMessages(rowResults)
}

/**
 * @param {BehaviourCsvUploadResult} result
 * @param {(key: string, values?: Record<string, number>) => string} t
 * @returns {{ isError: boolean, title: string, message: string }}
 */
export function buildBehaviourCsvUploadResultMessage(result, t) {
  const total = Number(result?.totalRows) || 0
  const inserted = Number(result?.inserted) || 0
  const failed = Number(result?.failed) || 0
  const apiTitle = String(result?.title ?? "").trim()
  const apiMessage = String(result?.summary ?? result?.message ?? "").trim()

  if (inserted > 0 && failed === 0) {
    return {
      isError: false,
      title: apiTitle || t("behaviour_settings.upload_csv_success_title"),
      message:
        apiMessage ||
        t("behaviour_settings.upload_csv_success", { inserted, total })
    }
  }

  if (inserted > 0 && failed > 0) {
    return {
      isError: false,
      title:
        apiTitle || t("behaviour_settings.upload_csv_partial_success_title"),
      message:
        apiMessage ||
        t("behaviour_settings.upload_csv_partial_success", {
          inserted,
          failed,
          total
        })
    }
  }

  return {
    isError: true,
    title: apiTitle || t("behaviour_settings.upload_csv_all_failed_title"),
    message:
      apiMessage ||
      t("behaviour_settings.upload_csv_all_failed", { failed, total })
  }
}

/**
 * @param {BehaviourCsvUploadResult} result
 * @returns {boolean}
 */
export function hasBehaviourCsvUploadInsertions(result) {
  return Number(result?.inserted) > 0
}

/**
 * @param {BehaviourCsvUploadResult} result
 * @returns {boolean}
 */
export function hasBehaviourCsvUploadFailures(result) {
  return Number(result?.failed) > 0
}
